import React, { useState } from 'react';
import { create } from 'zustand';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';
import { Button } from './Button';

interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for deletes and other irreversible actions. */
  danger?: boolean;
}

interface ConfirmState {
  pending: (ConfirmOptions & { resolve: (ok: boolean) => void }) | null;
  ask: (opts: ConfirmOptions) => Promise<boolean>;
  settle: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  pending: null,
  ask: (opts) => new Promise<boolean>(resolve => {
    get().pending?.resolve(false);
    set({ pending: { ...opts, resolve } });
  }),
  settle: (ok) => {
    get().pending?.resolve(ok);
    set({ pending: null });
  },
}));

/** Promise-based replacement for window.confirm():
 *  `if (!(await confirmDialog({ title: 'Delete pipeline?', danger: true }))) return;` */
export const confirmDialog = (opts: ConfirmOptions): Promise<boolean> => useConfirmStore.getState().ask(opts);

/** Mounted once at the root of App. */
export const ConfirmDialogHost: React.FC = () => {
  const { t } = useTranslation();
  const pending = useConfirmStore(s => s.pending);
  const settle = useConfirmStore(s => s.settle);
  const [busy, setBusy] = useState(false);
  if (!pending) return null;
  return (
    <Modal
      open
      size="sm"
      title={pending.title}
      onClose={() => settle(false)}
      footer={<>
        <Button onClick={() => settle(false)}>{pending.cancelLabel ?? t('common.cancel', 'Cancel')}</Button>
        <Button
          variant={pending.danger ? 'danger' : 'primary'}
          loading={busy}
          autoFocus
          onClick={() => { setBusy(true); settle(true); setBusy(false); }}
        >
          {pending.confirmLabel ?? t('common.confirm', 'Confirm')}
        </Button>
      </>}
    >
      {pending.message}
    </Modal>
  );
};

export default ConfirmDialogHost;
